import { useRef, useState } from "react";
import CustomCard from "components/common/CustomCard";
import CardHeader from "components/common/CardHeader";
import { Row, Spinner } from "react-bootstrap";
import { displayNotification } from "helpers";
import ConceptsFilters from "../concepts-cleaning/ConceptsFilters";
import CardBody from "components/common/CardBody";
import Column from "components/common/Column";
import VirtualisedUMCListComponent from "./VirtualisedUMCListComponent";
import Pill from "components/common/Pill";
import { deleteUmbrellaConcepts, editUmbrellaConcept, getConcepts, getConceptUmbrellaConcepts, getRawTextConcepts, mergeUmbrellaConcepts, tagUmbrellaConcepts } from "./ApiHelper";
import { getRawText, getTags, sortBy } from "../concepts-cleaning/ApiHelper";
import ConceptsActionsButton from "../concepts-cleaning/ConceptsActionsButton";

const overFlowStyle = {
    'min-height': '300px',
    'max-height': '500px',
    overflowY: 'auto',
    overflowX: 'hidden',
};

const UmbrellaConceptsCleaning = () => {

    const [loadingConcepts, setLoadingConcepts] = useState(false);
    const [loadingRawText, setLoadingRawText] = useState(false);
    const [loadingTags, setLoadingTags] = useState(false);
    const [umbrellaConcepts, setUmbrellaConcepts] = useState([]);
    const [concepts, setConcepts] = useState([]);
    const [conceptUmbrellaConcepts, setConceptUmbrellaConcepts] = useState([]);
    const [checkedBag, setCheckedBag] = useState([]);
    const [rawText, setRawText] = useState([]);
    const [tags, setTags] = useState([]);
    const [textConcepts, setTextConcepts] = useState([]);
    const [currentSelected, setCurrentSelected] = useState('');
    const [currentSelectedConcept, setCurrentSelectedConcept] = useState('');
    const [currentSelectedText, setCurrentSelectedText] = useState('');
    const [searchedUmbrellaConcepts, setSearchedUmbrellaConcepts] = useState([]);
    const conceptsFiltersRef = useRef(null);

    const handleChecked = (id, isChecked = false) => {
        if (isChecked) {
            setCheckedBag([...checkedBag, id]);
        } else {
            setCheckedBag(checkedBag.filter(item => item !== id));
        }
    }

    const checkAll = () => {
        setCheckedBag(searchedUmbrellaConcepts.map(concept => concept.id));
    }

    const unCheckAll = () => {
        setCheckedBag([]);
    };

    const totalChecked = () => {
        return checkedBag.length;
    }

    const isChecked = (id) => checkedBag.includes(id);

    const handleCurrentSelected = (id) => setCurrentSelected(id);

    const reloadData = (term = '') => {
        const filteredData = umbrellaConcepts.filter(item =>
            item.umbrella_concept?.toLowerCase().includes(term?.toLowerCase())
        );
        setSearchedUmbrellaConcepts(filteredData);
    }

    const sortData = (key, orderBy) => setSearchedUmbrellaConcepts([...sortBy(searchedUmbrellaConcepts, key, orderBy)]);

    const loadConcepts = async (ids) => {
        try {
            setLoadingConcepts(true);
            const data = await getConcepts(ids);
            if (data.success) {
                setConcepts(data.data);
            }
            setLoadingConcepts(false);
        } catch (err) {
            console.error({ err });
            setLoadingConcepts(false);
        }
    }

    const handleConceptsClick = async (id) => {
        try {
            setLoadingRawText(true);
            setCurrentSelectedConcept(id);
            const text = await getRawText(id);
            if (text.success) {
                setRawText(text.data);
            }
            const data = await getConceptUmbrellaConcepts(id);
            if (data.success) {
                setConceptUmbrellaConcepts(data.data);
            }
            setLoadingRawText(false);
        } catch (err) {
            console.error({ err });
            setLoadingRawText(false);
        }
    }

    const handleRawTextClick = async (id) => {
        try {
            setLoadingTags(true);
            const res = await getTags(id);
            if (res.success) {
                setCurrentSelectedText(id);
                setTags(res.data);
            }
            const text = await getRawTextConcepts(id);
            if (text.success) {
                setTextConcepts(text.data);
            }
            setLoadingTags(false);
        } catch (err) {
            console.error({ err });
            setLoadingTags(false);
        }
    }

    const handleDelete = async () => {
        try {
            if (checkedBag.length < 1) {
                displayNotification('info', 'You must select at least one umbrella concept');
                return;
            }
            if (!window.confirm("You are about to delete selected umbrella concepts. Are you sure?")) {
                return;
            }
            const ids = checkedBag.map(id => id.toString());
            const isDeleted = await deleteUmbrellaConcepts(ids);
            if (isDeleted.success) {
                const filteredData = searchedUmbrellaConcepts.filter(item => !checkedBag.includes(item.id));
                setSearchedUmbrellaConcepts(filteredData);
                setCheckedBag([]);
            }
        } catch (error) {
            console.error(error);
        }
    }

    const handleEdit = async (concept_name = '') => {
        try {
            if (checkedBag.length !== 1) {
                displayNotification('info', 'You must select one umbrella concept');
                return;
            }
            if (concept_name === '') {
                displayNotification('error', 'Concept name is required');
                return;
            }
            const isUpdated = await editUmbrellaConcept({
                ids: checkedBag[0],
                umbrella_concept: concept_name
            });
            if (isUpdated.success) {
                setSearchedUmbrellaConcepts(searchedUmbrellaConcepts.map(x => {
                    if (x.id === checkedBag[0]) {
                        x.umbrella_concept = concept_name;
                    }
                    return x;
                }));
            }
        } catch (error) {
            console.error(error);
        }
    }

    const handleMerge = async (concept_name = '') => {
        try {
            if (checkedBag.length < 1) {
                displayNotification('info', 'You must select atleast two umbrella concept');
                return;
            }
            if (concept_name === '') {
                displayNotification('error', 'Umbrella concept name is required');
                return;
            }
            const isUpdated = await mergeUmbrellaConcepts(checkedBag, concept_name);
            if (isUpdated.success) {
                if (conceptsFiltersRef.current) {
                    conceptsFiltersRef.current.loadConcepts();
                }
            }
        } catch (error) {
            console.error(error);
        }
    }

    const handleTaging = async (tag = '', id = null) => {
        try {
            if (checkedBag.length < 1 && !id) {
                displayNotification('info', 'You must select atleast one umbrella concept');
                return;
            }
            let ids = id ? id : checkedBag;
            tag = (tag.charAt(0)).toUpperCase() + tag.slice(1);
            const res = await tagUmbrellaConcepts(ids, tag);
            if (res.success) {
                let temp = [...searchedUmbrellaConcepts];
                temp = temp.map(t => {
                    if (id ? id === parseInt(t.id) : checkedBag.includes(t.id)) {
                        t.tag_type = tag;
                    }
                    return t;
                })
                setSearchedUmbrellaConcepts(temp);
            }
        } catch (error) {
            console.error(error);
        }
    }

    const handleCheckSimilarityIndex = async () => {
        try {
            if (checkedBag.length < 1 || checkedBag.length > 1) {
                displayNotification('info', 'You must select one umbrella concept');
                return;
            }
            const tempData = [...searchedUmbrellaConcepts]
            const currentSelected = tempData.find(x => checkedBag.includes(x.id));
            if (conceptsFiltersRef.current) {
                await conceptsFiltersRef.current.loadConcepts(true, currentSelected.umbrella_concept);
            }
        } catch (error) {
            console.error(error);
        }
    }

    const restAll = () => {
        setConcepts([]);
        setConceptUmbrellaConcepts([]);
        setRawText([]);
        setTextConcepts([]);
        setTags([]);
        setCurrentSelected('');
        setCheckedBag([]);
    }

    return (
        <div>
            <ConceptsFilters
                reloadData={(value) => {
                    setSearchedUmbrellaConcepts(value);
                    setUmbrellaConcepts(value);
                    restAll();
                }}
                isUmbrellaConcepts={true}
                title={'Qualitative Cleaning - Umbrella Concepts'}
                ref={conceptsFiltersRef}
            />
            <CustomCard>
                <CardHeader>
                    <ConceptsActionsButton
                        reloadData={reloadData}
                        checkAll={checkAll}
                        unCheckAll={unCheckAll}
                        totalChecked={totalChecked}
                        sortData={sortData}
                        handleDelete={handleDelete}
                        handleEdit={handleEdit}
                        handleMerge={handleMerge}
                        handleTaging={handleTaging}
                        handleCheckSimilarityIndex={handleCheckSimilarityIndex}
                    />
                </CardHeader>
                <CardBody className='px-4 py-0'>
                    <Row className="border-bottom border-secondary border-2 bg-primary">
                        <Column col={3} className="border-end border-secondary px-3 border-2"><h1 className="p-3 text-white">Umbrella Concepts - ({searchedUmbrellaConcepts.length})</h1></Column>
                        <Column col={3} className="border-end border-secondary px-3 border-2"><h1 className="p-3 text-white">Concepts - ({concepts.length})</h1></Column>
                        <Column col={4} className="border-end border-secondary px-3 border-2"><h1 className="p-3 text-white">Raw Text - ({rawText.length})</h1></Column>
                        <Column col={2}><h1 className="p-3 text-white">Tags - ({tags.length})</h1></Column>
                    </Row>
                    {
                        searchedUmbrellaConcepts && searchedUmbrellaConcepts.length > 0 ? (
                            <Row>
                                <Column col={3} className="border-end border-secondary px-0 border-2" style={overFlowStyle}>
                                    <VirtualisedUMCListComponent
                                        data={searchedUmbrellaConcepts}
                                        handleChecked={handleChecked}
                                        isChecked={isChecked}
                                        loadConcepts={loadConcepts}
                                        currentSelected={currentSelected}
                                        handleCurrentSelected={handleCurrentSelected}
                                        handleTag={handleTaging}
                                    />
                                </Column>
                                <Column col={3} className="d-flex flex-column border-end border-secondary px-3 border-2" style={overFlowStyle}>
                                    <div className="d-flex flex-column align-items-start py-3">
                                        {
                                            loadingConcepts ? <Spinner message="Loading data..." /> :
                                                concepts.map((concept, index) => (
                                                    <Pill
                                                        className='p-1 m-1 cursor-pointer'
                                                        variant={`${currentSelectedConcept === concept.id ? 'primary' : 'secondary'}`}
                                                        key={index}
                                                        onClick={async () => {
                                                            await handleConceptsClick(concept.id)
                                                        }}
                                                    >
                                                        {concept.concept}
                                                    </Pill>
                                                ))
                                        }
                                        {
                                            conceptUmbrellaConcepts.length > 0 && (
                                                <div>
                                                    <h3>Umbrella Concepts</h3>
                                                    {
                                                        conceptUmbrellaConcepts.map((umbrellaConcept, index) => (
                                                            <Pill
                                                                className='p-1 m-1'
                                                                variant="info"
                                                                key={index}
                                                            >
                                                                {umbrellaConcept.umb_concept}
                                                            </Pill>
                                                        ))
                                                    }
                                                </div>
                                            )
                                        }
                                    </div>
                                </Column>
                                <Column col={4} className="d-flex flex-column border-end border-secondary px-3 border-2" style={overFlowStyle}>
                                    <div className="d-flex flex-column align-items-start py-3">
                                        {
                                            loadingRawText ? <Spinner message="Loading data..." /> :
                                                rawText.map((text, index) => (
                                                    <Pill
                                                        className='p-1 m-1 cursor-pointer text-wrap text-start'
                                                        variant={`${currentSelectedText === text.id ? 'primary' : 'secondary'}`}
                                                        key={index}
                                                        onClick={() => handleRawTextClick(text.id)}
                                                    >
                                                        {text.heading_text}
                                                    </Pill>
                                                ))
                                        }
                                        {
                                            textConcepts.length > 0 && (
                                                <div>
                                                    <h3>Concepts</h3>
                                                    {
                                                        textConcepts.map((concept, index) => (
                                                            <Pill
                                                                className='p-1 m-1'
                                                                variant="info"
                                                                key={index}
                                                            >
                                                                {concept.concept}
                                                            </Pill>
                                                        ))
                                                    }
                                                </div>
                                            )
                                        }
                                    </div>
                                </Column>
                                <Column col={2} className="d-flex flex-column" style={overFlowStyle}>
                                    <div className="d-flex flex-column align-items-start py-3">
                                        {
                                            loadingTags ? <Spinner message="Loading data..." /> :
                                                tags.map((tag, index) => (
                                                    <Pill
                                                        className='p-1 m-1 text-wrap'
                                                        variant='secondary'
                                                        key={index}
                                                    >
                                                        {tag.tag}
                                                    </Pill>
                                                ))
                                        }
                                    </div>
                                </Column>
                            </Row>
                        ) : (
                            <Row>
                                <Column col={12} className="text-center">
                                    No data available.
                                </Column>
                            </Row>
                        )
                    }

                </CardBody>
            </CustomCard>
        </div>
    );
}

export default UmbrellaConceptsCleaning;